import { AxiosResponse } from 'axios'
import { LocalStorage, Platform } from 'quasar'
import {
  AuthenticatedUserDto,
  getAdminUrl,
  getBillingPlan,
  getStripeUrl,
  LoginResultDto,
  UserDto,
  validInt
} from 'booksprout'
import { useAxios } from '../axios/axiosModule'
import { useApollo } from '../apollo/apolloModule'
import { useCookies } from '../cookies/cookiesModule'
import { useUi } from '../ui/uiModule'
import { identifyBeaconUser, resetBeaconUser } from '../../helpScout/beacon'
import { makeUserResult } from '../user/makeUserResult'
import authStoreGetters from './store/getters'
import {
  _authLoad,
  _authLogout,
  _authSuccess,
  _setAuthExpired,
  _setConsentFailed,
  _setConsentNeeded,
  _setUser
} from './store/mutations'

/**
 * Replaces AuthService *and* the Vuex auth actions.
 *
 * The two belonged together: every action of substance reached
 * `container.get('AuthService')` for its API call and then committed the result, so splitting the
 * request from the state write across a service and an action bought nothing. This is both halves.
 *
 * The Vuex actions now delegate here, so the ~79 `dispatch(...)` sites are untouched until 4c, and
 */

const userTokenKey = 'user-token'
const userTokenExpiresKey = 'user-token-expires'
const arcFiltersKey = 'userSelectedArcFilters'

/**
 * Both writers pass their own `expires`: the mirror's lifetime is the token's, so it can never
 * outlive the session it describes. [sc-27671]
 */
const cookieOptions = {
  path: '/'
}

/**
 * iOS Capacitor keeps the login token in LocalStorage; every other platform leaves it in the
 * httpOnly cookie and stores only the expiry. The `getItem` check guards the harness, where the
 * plugin is not installed.
 */
const usesLocalStorageToken = () =>
  Platform.is.capacitor && Platform.is.ios && typeof LocalStorage.getItem === 'function'

/**
 * Only the id is ever sent, and `null` is how an active mimic is cleared.
 */
export type MimicTarget = Pick<UserDto, 'id'> | null

/**
 * `AUTH_REQUIRED` was declared by *two* Vuex modules, so one dispatch did two jobs: auth cleared the
 * session, and ui persisted the forward url and sent the visitor to /login so they land back where
 * they were. Both halves are plain calls now — this owns the session clear and hands the rest to
 * uiModule.
 */
export interface AuthRequiredPayload {
  forwardUrl: string
  router?: any
}

export function authenticationService () {
  const axios = () => useAxios().axios()

  // ── API ────────────────────────────────────────────────────────────────────────────────────
  // Ported from AuthService unchanged.

  const login = (
    emailAddress: string,
    password: string,
    extendLogin: boolean,
    reinstateConsent: boolean
  ): Promise<LoginResultDto | null> => {
    return axios().post('auth/login', { emailAddress, password, extendLogin, reinstateConsent })
      .then((r: { status: number, data: LoginResultDto }) => {
        if (r !== void 0 && r.status === 401) {
          return null
        }

        return r.data
      })
  }

  const logout = (): Promise<any> => axios().post('auth/logout')

  const setMimicUser = (mimicUser: MimicTarget): Promise<LoginResultDto> => {
    return axios().post('auth/setMimicUser', { id: mimicUser ? mimicUser.id : null })
      .then((r: AxiosResponse) => r.data)
  }

  const signUp = (emailAddress: string, password: string, agreeToMarketing: boolean, forward?: string) => {
    return axios().post('auth/sign-up', { emailAddress, password, agreeToMarketing, forward })
  }

  const resetPassword = (emailAddress: string) => {
    return axios().post('auth/request-password-reset', { emailAddress })
  }

  const userResetPassword = (oldPassword: string, newPassword: string) => {
    return axios().post('auth/user-password-reset', { oldPassword, newPassword })
  }

  const completeResetPassword = (token: string, newPassword: string) => {
    return axios().post('auth/complete-password-reset', { token, newPassword })
  }

  const resendEmailVerificationEmail = (emailAddress: string, forward?: string) => {
    return axios().post('auth/resend-verification-email', { emailAddress, forward })
  }

  const updateUserEmailAddress = (
    oldEmailAddress: string,
    newEmailAddress: string,
    forward?: string
  ): Promise<UserDto> => {
    return axios().post('auth/update-user-email', { oldEmailAddress, newEmailAddress, forward })
      .then(response => response.data)
  }

  const completeVerificationEmail = (token: string): Promise<UserDto> => {
    return axios().post('auth/complete-verification-email', { token }).then((r: { data: UserDto }) => r.data)
  }

  const completeOAuthSignUpWithEmail = (emailAddress: string): Promise<LoginResultDto> => {
    return axios().post('auth/complete-oauth-login-with-email', { emailAddress, source: void 0 })
      .then((r: { data: LoginResultDto }) => r.data)
  }

  const updateUserSelectedRole = (role: number): Promise<LoginResultDto> => {
    const agreedToAllMarketingUpdates = LocalStorage.getItem<boolean>('agreeToMarketing') || false

    return axios().post('auth/update-user-role', { role, agreedToAllMarketingUpdates })
      .then((r: { status: number, data: LoginResultDto }) => r.data)
  }

  const getCsrfToken = (): Promise<string> => {
    return axios().get('auth/getCsrfToken', { withCredentials: true }).then((r: { data: string }) => r.data)
  }

  const validateToken = (token: string, loginType: number, source: string, nonce?: string) => {
    return axios().post('auth/validateToken', { token, loginType, source, nonce })
      .then((r: { status: number, data: LoginResultDto }) => {
        if (r !== void 0 && r.status === 401) {
          return null
        }

        return r.data
      })
      .catch((e: any) => {
        console.error('validateToken error: ', e)
        return null
      })
  }

  const getAuthenticatedUser = (): Promise<AuthenticatedUserDto | undefined> => {
    return axios().post('auth/authenticated-user')
      .then((r: { status: number, data: AuthenticatedUserDto }) => {
        if (!r.data.user) {
          return void 0
        }

        return {
          user: makeUserResult(r.data.user),
          mimicUser: r.data.mimicUser ? makeUserResult(r.data.mimicUser) : void 0,
          // The session itself is httpOnly, so this is the only way the client can learn how long it
          // has left — see the recovery branch in `load`. [sc-27671]
          tokenExpires: r.data.tokenExpires
        }
      })
  }

  const checkForUpdate = (currentVersion: string, platform: string, env: string) => {
    return axios().post('auth/check-for-update', { currentVersion, platform, env }).then(r => r.data)
  }

  const sendPhoneVerification = (phoneNumber: string): Promise<void> => {
    return axios().post('auth/send-phone-verification', { phoneNumber })
  }

  const completeVerificationPhone = (code: string): Promise<UserDto> => {
    return axios().post('auth/complete-verification-phone', { code }).then((r: { data: UserDto }) => r.data)
  }

  const resendPhoneVerification = (): Promise<void> => axios().post('auth/resend-verification-phone')

  /**
   * A no-op. It rebuilt the axios instance after login so it would pick up the new token; both
   * transports now read auth per request - axiosModule in its request interceptor, apolloModule in its
   * auth link - so there is nothing to rebuild. Until sc-28630 the Apollo half of that was not true,
   * and its frozen headers went unnoticed because this comment said so. Kept because `authSuccess` and
   * AUTH_SUCCESS still call it.
   */
  const onLoggedIn = () => void 0

  // ── Flows ──────────────────────────────────────────────────────────────────────────────────
  // Ported from the Vuex auth actions.

  const setUser = (user: UserDto) => {
    _setUser(user)

    /**
     * Read by index.html. A genuine side effect on a global, carried over as-is.
     */
    ;(window as any).GlobalUserData = {
      name: user.name,
      emailAddress: user.emailAddress,
      image: user.image,
      plan: getBillingPlan(user.authorRole),
      userSince: (user.createdDate as unknown as string).slice(0, 10),
      stripeUrl: getStripeUrl(user.stripeCustomerId),
      adminUrl: getAdminUrl(user.id, user.name || user.emailAddress)
    }

    // Hand the same details to Help Scout. This function is the one place that knows who is signed in
    // - it covers sign in and the boot time refresh - so Beacon tracks the session rather than the
    // page load. Mimicking is unaffected: the login result carries the real user here and the
    // mimicked one separately, so support still sees the person actually at the keyboard [sc-27755].
    identifyBeaconUser((window as any).GlobalUserData)
  }

  const setAuthExpired = (hasExpired: boolean) => {
    _setAuthExpired(hasExpired)
  }

  const setConsentFailed = (payload: any) => {
    _setConsentFailed(payload)
  }

  const setConsentNeeded = (payload: any) => {
    _setConsentNeeded(payload)
  }

  /**
   * Page reload: seed the state from the cookie (or LocalStorage on iOS) so API calls can be made,
   * then replace it with the server's answer once it arrives.
   */
  const load = async (): Promise<void> => {
    const isLocalStorageToken = usesLocalStorageToken()

    const data = isLocalStorageToken ? LocalStorage.getItem<string>(userTokenKey) : void 0
    const expires = isLocalStorageToken
      ? LocalStorage.getItem<number>(userTokenExpiresKey)
      : parseInt(useCookies().get(userTokenExpiresKey))

    const loginState = {
      user: new UserDto(),
      token: { data, expires },
      mimicUser: new UserDto()
    }

    _authLoad(loginState as any)

    const nowInSeconds = new Date().getTime() / 1000

    if ((loginState.token.expires || 0) > nowInSeconds) {
      const authenticatedUser = await getAuthenticatedUser()

      if (authenticatedUser) {
        loginState.user = authenticatedUser.user
        if (authenticatedUser.mimicUser) {
          loginState.mimicUser = authenticatedUser.mimicUser as UserDto
        }

        _authLoad(loginState as any)
      }
    } else if (!isLocalStorageToken && !process.env.SERVER) {
      /**
       * No usable expiry — but that expiry is only a mirror of the session, and the session itself is
       * in an httpOnly cookie we cannot read. A missing mirror therefore does NOT mean signed out: it
       * means we never saw the login happen. That is the normal case for the OAuth callback's
       * no-opener fallback, and it left readers looping through sign-in against a perfectly live
       * session (sc-27671). Only the API can see the session cookie, so ask it rather than guessing.
       *
       * Native iOS keeps its token in device storage instead of a cookie, so there is nothing to
       * recover there, and this is skipped during SSR so anonymous page renders do not pay for a call
       * the browser will make on hydration anyway.
       */
      const authenticatedUser = await getAuthenticatedUser()
      const sessionExpires = authenticatedUser?.tokenExpires || 0

      if (validInt(authenticatedUser?.user?.id) && sessionExpires > nowInSeconds) {
        loginState.user = authenticatedUser?.user as UserDto
        loginState.token.expires = sessionExpires
        if (authenticatedUser?.mimicUser) {
          loginState.mimicUser = authenticatedUser.mimicUser as UserDto
        }

        _authLoad(loginState as any)

        // Re-establish the mirror so the rest of this session takes the cheap path above. Its own
        // lifetime is the token's, so it can never outlive what it describes.
        useCookies().set(
          userTokenExpiresKey,
          sessionExpires.toString(),
          { ...cookieOptions, expires: new Date(sessionExpires * 1000) }
        )
      }
    }
  }

  /**
   * Rejects on a failed or consent-blocked login. Callers rely on that: the login page treats a
   * rejection as "do not navigate".
   */
  const authSuccess = (payload: any): Promise<LoginResultDto | null> => {
    if (payload.loginResult === null) {
      return Promise.reject()
    }

    if (payload.loginResult.termsConsentFailed === true) {
      setConsentFailed(payload)
      return Promise.reject()
    }

    if (payload.loginResult.termsConsentNeeded) {
      setConsentNeeded(payload)
      return Promise.reject()
    }

    setUser(payload.loginResult.user)

    if (usesLocalStorageToken()) {
      LocalStorage.set(userTokenKey, payload.loginResult.token.data)
      LocalStorage.set(userTokenExpiresKey, payload.loginResult.token.expires)
    } else {
      useCookies().set(
        userTokenExpiresKey,
        payload.loginResult.token.expires.toString(),
        { ...cookieOptions, expires: new Date(payload.loginResult.token.expires * 1000) }
      )
    }

    _authSuccess(payload.loginResult)
    setAuthExpired(false)
    onLoggedIn()

    /**
     * `Promise.resolve`, not a bare `return payload.loginResult`.
     *
     * The Vuex action this replaced returned the value bare, which was fine there: `store.dispatch()`
     * always wraps an action's return in a promise, so `dispatch(AUTH_SUCCESS, …).then(…)` worked. A
     * plain function returns exactly what it returns, so every caller chaining `.then` off this — the
     * role picker, the OAuth logins, plan selection — died with "authSuccess(...).then is not a
     * function" the moment the happy path ran. `payload: any` is why tsc could not see it: the bare
     * value was assignable to the declared `Promise<…>` return type.
     */
    return Promise.resolve(payload.loginResult)
  }

  const request = (payload: any): Promise<LoginResultDto | null> => {
    return login(payload.emailAddress, payload.password, payload.extendLogin, payload.reinstateConsent)
      .then(loginResult => authSuccess({ loginResult, payload }))
  }

  const clearSession = () => {
    if (usesLocalStorageToken()) {
      if (LocalStorage.has(userTokenKey)) {
        LocalStorage.remove(userTokenKey)
      }
      if (LocalStorage.has(userTokenExpiresKey)) {
        LocalStorage.remove(userTokenExpiresKey)
      }
    }

    /**
     * The arc filter snapshot belongs to this login: it must not be applied to the next account on
     * this device, nor linger to be mistaken for filters chosen while logged out (sc-27202).
     */
    if (typeof LocalStorage.remove === 'function' && LocalStorage.has(arcFiltersKey)) {
      LocalStorage.remove(arcFiltersKey)
    }

    // Beacon keeps the identified visitor until it is told otherwise, so without this the next person
    // to sign in on this device inherits the previous user's identity and conversations [sc-27755].
    resetBeaconUser()

    _authLogout()
  }

  const logoutRequest = () => {
    /**
     * Send the logout while the token is still in the store, so the request carries it and the server
     * can add it to the logout blacklist. clearSession() wipes the token — LocalStorage and store —
     * and on iOS that token rides only in the Authorization header, so clearing first left the
     * request with an empty bearer: the jwt-guarded route answered 401 and the token was never
     * revoked, staying valid until it expired. [sc-28633]
     *
     * Never rejects. Every caller redirects to /login in a bare `.then`, and the axios interceptor
     * rejects a 401 - so a failed request (an already-revoked or expired token, or no connection)
     * left the reader on the page with their session cleared and no redirect. Revocation is best
     * effort; signing out of this device is not. The session is cleared either way.
     */
    return logout()
      .catch(() => void 0)
      .finally(() => clearSession())
  }

  const authRequired = (data?: AuthRequiredPayload) => {
    clearSession()

    // Returned so the route guards keep awaiting the redirect exactly as they awaited the dispatch
    // this replaced.
    return useUi().ui.authRequired(data as any)
  }

  const invalid = () => {
    // Replaced by a translated version and an upsell prompt eventually; carried over as-is.
    useUi().ui.notifyError('You do not have permission to do this.')
  }

  const mimic = (user: MimicTarget) => {
    /**
     * The server re-issues the tokens with the mimicked user baked into the signed auth token, so
     * the result goes through authSuccess like any other login.
     */
    return setMimicUser(user).then(loginResult => authSuccess({ loginResult }))
  }

  const refreshCsrfToken = () => useApollo().refreshToken()

  return {
    // api
    login,
    logout,
    setMimicUser,
    signUp,
    resetPassword,
    userResetPassword,
    completeResetPassword,
    resendEmailVerificationEmail,
    updateUserEmailAddress,
    completeVerificationEmail,
    completeOAuthSignUpWithEmail,
    updateUserSelectedRole,
    getCsrfToken,
    validateToken,
    getAuthenticatedUser,
    checkForUpdate,
    sendPhoneVerification,
    completeVerificationPhone,
    resendPhoneVerification,
    onLoggedIn,
    // flows
    load,
    request,
    authSuccess,
    clearSession,
    logoutRequest,
    authRequired,
    invalid,
    mimic,
    setUser,
    setAuthExpired,
    setConsentFailed,
    setConsentNeeded,
    refreshCsrfToken,
    // state, for callers that already reach through the service
    get isAuthenticated () {
      return authStoreGetters.isAuthenticated
    }
  }
}

